import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MessageSquare, Plus, Calendar, Hash } from "lucide-react";
import axios from "axios";
import AppContext from "../../../../context/AppContext";
import SupStatusBadge from "../../../../components/general/SupportSharedFiles/SupStatusBadge";
import SupEmptyState from "../../../../components/general/SupportSharedFiles/SupEmptyState";
import SupLoader from "../../../../components/general/SupportSharedFiles/SupLoader";
import "../../../../assets/css/general/Support/supshare.css";
import "../../../../assets/css/customer/customerSupport/cussupportticketlist.css";


const FILTERS = ["all", "open", "in_progress", "resolved"];

const CusSupportTicketsList = () => {
  const { baseUrl, authToken } = useContext(AppContext);
  const navigate = useNavigate();

  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchTickets = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/customer/support/tickets`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params: { status: filter },
      })
      .then((res) => {
        setTickets(res.data.supporttickets || []);
        setError("");
      })
      .catch((err) => {
        console.error(err.response?.data || err.message);
        setError("Failed to load your tickets.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTickets();
  }, [filter, authToken]);

  const filtered = tickets.filter((t) => {
    const q = search.toLowerCase();
    return (
      t.category?.toLowerCase().includes(q) ||
      String(t.id).includes(q) ||
      t.description?.toLowerCase().includes(q)
    );
  });

  const getBookingName = (ticket) => {
    if (ticket.jjbooking?.Jjpackage?.package_name)
      return ticket.jjbooking.Jjpackage.package_name;
    if (ticket.jjbooking?.jjroom?.room_type)
      return ticket.jjbooking.jjroom.room_type;
    return ticket.jjbooking ? `Booking #${ticket.jjbooking.id}` : "—";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-PK", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  return (
    <div className="cus-stl-page">
      {/* Header */}
      <div className="cus-stl-header">
        <div>
          <h2 className="cus-stl-heading">My Tickets</h2>
          <p className="cus-stl-subtext">All your support requests in one place</p>
        </div>
        <div className="cus-stl-header__right">
          <div className="cus-stl-search-wrap">
            <Search size={14} className="cus-stl-search-icon" />
            <input
              className="cus-stl-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tickets..."
            />
          </div>
          <button
            className="cus-stl-create-btn"
            onClick={() => navigate("/customer/support/create")}
          >
            <Plus size={15} /> New Ticket
          </button>
        </div>
      </div>

      {error && <div className="sup-error-banner">{error}</div>}

      {/* Filter Tabs */}
      <div className="cus-stl-filters">
        {FILTERS.map((f) => (
          <button
            key={f}
            className={`cus-stl-filter-btn ${filter === f ? "cus-stl-filter-btn--active" : ""}`}
            onClick={() => setFilter(f)}
          >
            {f.replace("_", " ")}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <SupLoader label="Loading your tickets..." />
      ) : filtered.length === 0 ? (
        <SupEmptyState
          icon={MessageSquare}
          title="No tickets found"
          subtitle={search ? "Try a different search keyword" : "Create a ticket if you have an issue"}
        />
      ) : (
        <div className="cus-stl-list">
          {filtered.map((ticket, i) => (
            <div
              key={ticket.id}
              className="cus-stl-card"
              style={{ animationDelay: `${i * 0.04}s` }}
            >
              <div className="cus-stl-card__top">
                <div className="cus-stl-card__meta">
                  <span className="cus-stl-ticket-id">
                    <Hash size={11} />{ticket.id}
                  </span>
                  <SupStatusBadge status={ticket.status} />
                </div>
                <button
                  className="cus-stl-view-btn"
                  onClick={() => navigate(`/customer/support/tickets/${ticket.id}`)}
                >
                  <MessageSquare size={13} /> View Chat
                </button>
              </div>

              <p className="cus-stl-card__title">{ticket.category}</p>
              <p className="cus-stl-card__booking">{getBookingName(ticket)}</p>
              <p className="cus-stl-card__desc">{ticket.description}</p>

              <div className="cus-stl-card__footer">
                <span className="cus-stl-card__date">
                  <Calendar size={12} /> {formatDate(ticket.created_at)}
                </span>
                <span className="cus-stl-card__against">
                  Against: <strong>{ticket.against}</strong>
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CusSupportTicketsList;